import { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import api, { BACKEND_URL } from '../api/axios';

const COLORS = { blue: "#2563EB", orange: "#F59E0B", dark: "#0D1B3D" };

export default function CreateOfferModal({
  jobPostings,
  onClose,
  onCreated,
  initialJobPostingId = "",
  initialJobApplicationId = "",
  initialCandidateId = "",
  initialCandidateName = "",
}) {
  const [jobPostingId, setJobPostingId] = useState(initialJobPostingId);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [form, setForm] = useState({
    job_application_id: initialJobApplicationId,
    candidate_id: initialCandidateId,
    candidate_name: initialCandidateName,
    salary: "",
    joining_date: "",
    reporting_manager: "",
    location: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!jobPostingId) {
      setApplicants([]);
      return;
    }
    setLoadingApplicants(true);
    api
      .get("/employer/job-applicants", { params: { job_posting_id: jobPostingId } })
      .then((res) => setApplicants(res.data))
      .catch(() => setError("Couldn't load applicants for this job."))
      .finally(() => setLoadingApplicants(false));
  }, [jobPostingId]);

  const handleApplicantChange = (jobApplicationId) => {
    const applicant = applicants.find(
      (a) => String(a.job_application_id) === String(jobApplicationId)
    );
    setForm((f) => ({
      ...f,
      job_application_id: jobApplicationId,
      candidate_id: applicant ? applicant.candidate_id : "",
      candidate_name: applicant ? applicant.candidate_name : f.candidate_name,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.candidate_name || !form.salary || !form.joining_date) {
      setError("Candidate name, salary, and joining date are required.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/offers", {
        job_application_id: form.job_application_id || null,
        candidate_id: form.candidate_id || null,
        candidate_name: form.candidate_name,
        salary: form.salary,
        joining_date: form.joining_date,
        reporting_manager: form.reporting_manager || null,
        location: form.location || null,
      });

      // Generate the PDF right away so the offer letter exists as soon as
      // the offer is created, instead of leaving it as a separate manual
      // step the employer has to remember to trigger later.
      try {
        await api.post(`/offers/${res.data.id}/generate-pdf`);
      } catch (pdfErr) {
        console.warn("Offer created but PDF generation failed.", pdfErr?.response?.data || pdfErr.message);
      }

      // Send the offer email right after creation so the employer doesn't
      // have to remember a separate "send" step. Non-blocking: the offer
      // still exists and can be sent manually later if this fails.
      try {
        await api.post(`/offers/${res.data.id}/send`);
      } catch (sendErr) {
        console.warn("Offer created but sending failed.", sendErr?.response?.data || sendErr.message);
      }

      onCreated();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't create the offer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div
          className="flex items-center justify-between rounded-t-xl px-6 py-4"
          style={{ backgroundColor: COLORS.dark }}
        >
          <h2 className="text-lg font-semibold text-white">Create Offer</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
              Job Posting (optional)
            </label>
            <select
              value={jobPostingId}
              onChange={(e) => setJobPostingId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
            >
              <option value="">No linked job posting</option>
              {jobPostings.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title}
                </option>
              ))}
            </select>
          </div>

          {jobPostingId && (
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
                Candidate from applicants
              </label>
              <select
                value={form.job_application_id}
                onChange={(e) => handleApplicantChange(e.target.value)}
                disabled={loadingApplicants}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100"
              >
                <option value="">
                  {loadingApplicants ? "Loading applicants..." : "Select an applicant"}
                </option>
                {applicants.map((a) => (
                  <option key={a.job_application_id} value={a.job_application_id}>
                    {a.candidate_name} — {a.application_status}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
              Candidate Name
            </label>
            <input
              type="text"
              value={form.candidate_name}
              onChange={(e) => setForm((f) => ({ ...f, candidate_name: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              placeholder="Full name as it should appear on the offer letter"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
                Salary (₹ / year)
              </label>
              <input
                type="number"
                min="0"
                value={form.salary}
                onChange={(e) => setForm((f) => ({ ...f, salary: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
                Joining Date
              </label>
              <input
                type="date"
                value={form.joining_date}
                onChange={(e) => setForm((f) => ({ ...f, joining_date: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
                Reporting Manager
              </label>
              <input
                type="text"
                value={form.reporting_manager}
                onChange={(e) => setForm((f) => ({ ...f, reporting_manager: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
                Location
              </label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              style={{ backgroundColor: COLORS.blue }}
            >
              {submitting ? "Creating..." : "Create Draft Offer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}